import { Link } from "react-router-dom";
import { 
  Building2, Car, Plane, GraduationCap, Map, Building, FileText, 
  ArrowRight, Leaf, Heart, Sparkles, Globe, TreePine, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";

const programs = [
  { 
    name: "GreenStay", 
    icon: Building2, 
    gradient: "from-emerald-500 to-teal-600",
    lightBg: "bg-emerald-50",
    slug: "greenstay", 
    tagline: "Eco-Friendly Accommodation",
    description: "Make your travel accommodation sustainable by offsetting the carbon footprint of your hotel stays. Every night at a hotel generates approximately 21kg of CO₂ from electricity, heating, water usage, and laundry services.",
    stats: "21kg CO₂ per night",
    impact: "Support renewable energy projects"
  },
  { 
    name: "GreenTrack", 
    icon: Car, 
    gradient: "from-green-500 to-emerald-600",
    lightBg: "bg-green-50",
    slug: "greentrack", 
    tagline: "Sustainable Mobility",
    description: "From daily commutes to weekend road trips, every kilometer counts. A typical petrol car emits 192g of CO₂ per kilometer. Offset your vehicle emissions and drive with a clearer conscience.",
    stats: "192g CO₂ per km (petrol)",
    impact: "Fund reforestation projects"
  },
  { 
    name: "FlyGreen", 
    icon: Plane, 
    gradient: "from-sky-500 to-blue-600",
    lightBg: "bg-sky-50",
    slug: "flygreen", 
    tagline: "Carbon-Neutral Aviation",
    description: "Air travel has a significant carbon footprint. A single domestic flight can emit 250kg+ of CO₂ per passenger. Fly responsibly by offsetting your aviation emissions with verified carbon credits.",
    stats: "~255g CO₂ per passenger-km",
    impact: "Invest in clean energy transition"
  },
  { 
    name: "GreenCampus", 
    icon: GraduationCap, 
    gradient: "from-teal-500 to-cyan-600",
    lightBg: "bg-teal-50",
    slug: "greencampus", 
    tagline: "Sustainable Education",
    description: "Educational institutions can lead by example. Help your school, college, or university achieve carbon neutrality and inspire the next generation of climate leaders through actionable sustainability.",
    stats: "Per student offset programs",
    impact: "Create climate-aware communities"
  },
  { 
    name: "GreenTourism", 
    icon: Map, 
    gradient: "from-lime-500 to-green-600",
    lightBg: "bg-lime-50",
    slug: "greentourism", 
    tagline: "Responsible Travel",
    description: "Tourism contributes ~8% of global emissions. Whether you're a tourist or a travel operator, make your travel experiences sustainable by offsetting the environmental impact of tourism activities.",
    stats: "Complete trip offset",
    impact: "Protect biodiversity hotspots"
  },
  { 
    name: "GreenBuild", 
    icon: Building, 
    gradient: "from-slate-600 to-gray-700",
    lightBg: "bg-slate-50",
    slug: "greenbuild", 
    tagline: "Sustainable Construction",
    description: "The construction industry accounts for nearly 40% of global CO₂ emissions. Offset your building project's carbon footprint and construct responsibly for a sustainable built environment.",
    stats: "~50kg CO₂ per sq.ft",
    impact: "Support green building initiatives"
  },
  { 
    name: "GreenPaper", 
    icon: FileText, 
    gradient: "from-amber-500 to-orange-600",
    lightBg: "bg-amber-50",
    slug: "greenpaper", 
    tagline: "Zero-Waste Office",
    description: "Despite digitalization, paper usage remains significant in offices. A single ream of paper is responsible for about 6kg of CO₂. Offset your organization's paper consumption and go truly green.",
    stats: "~6kg CO₂ per ream",
    impact: "Plant trees globally"
  },
];

export default function InitiativesPage() {
  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="initiatives-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-teal-200/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white text-emerald-700 px-5 py-2.5 rounded-full text-sm font-medium mb-8 shadow-lg shadow-emerald-100">
            <Sparkles className="h-4 w-4" />
            Climate Action Initiatives
          </div>
          <h1 className="font-montserrat text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Make Every Action
            <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Count for the Planet
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
            Choose how you want to contribute to climate action. Every program supports verified 
            carbon offset projects across India, helping us achieve Net Zero by 2070.
          </p>
          
          {/* Quick stats */}
          <div className="inline-flex flex-wrap justify-center gap-6 lg:gap-12 p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-emerald-600 mb-1">
                <Leaf className="h-5 w-5" />
                <span className="text-2xl font-bold">7+</span>
              </div>
              <p className="text-sm text-slate-600">Green Programs</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-teal-600 mb-1">
                <Globe className="h-5 w-5" />
                <span className="text-2xl font-bold">1000+</span>
              </div>
              <p className="text-sm text-slate-600">Contributors</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-cyan-600 mb-1">
                <TreePine className="h-5 w-5" />
                <span className="text-2xl font-bold">50K+</span>
              </div>
              <p className="text-sm text-slate-600">kg CO₂ Offset</p>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.map((program) => (
              <Link
                key={program.slug}
                to={`/programs/${program.slug}`}
                className="group relative"
                data-testid={`initiative-${program.slug}`}
              >
                <div className="h-full bg-white rounded-3xl border border-slate-100 overflow-hidden hover:shadow-2xl hover:shadow-emerald-100/50 transition-all duration-500 hover:-translate-y-2">
                  {/* Gradient Header */}
                  <div className={`bg-gradient-to-br ${program.gradient} p-8 text-white relative overflow-hidden`}>
                    {/* Background pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-0 right-0 w-32 h-32 border border-white/30 rounded-full -translate-y-1/2 translate-x-1/2" />
                      <div className="absolute bottom-0 left-0 w-24 h-24 border border-white/30 rounded-full translate-y-1/2 -translate-x-1/2" />
                    </div>
                    
                    <div className="relative">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-white/30 transition-all duration-300">
                        <program.icon className="h-8 w-8" />
                      </div>
                      <h3 className="font-montserrat text-2xl font-bold mb-1">
                        {program.name}
                      </h3>
                      <p className="text-white/80 font-medium">{program.tagline}</p>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <p className="text-slate-600 leading-relaxed mb-4">
                      {program.description}
                    </p>
                    
                    {/* Stats */}
                    <div className={`${program.lightBg} rounded-xl p-4 mb-4`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Carbon Impact</p>
                          <p className="font-semibold text-slate-800">{program.stats}</p>
                        </div>
                        <Zap className="h-5 w-5 text-slate-400" />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className="text-sm text-emerald-600 font-medium">
                        {program.impact}
                      </span>
                      <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-2 transition-all" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bharat Climate Initiative - Special Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-orange-50 via-white to-green-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl">
            {/* Tricolor border effect */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-white to-green-500" />
            
            <div className="grid lg:grid-cols-2">
              {/* Content */}
              <div className="p-8 lg:p-12">
                <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Heart className="h-4 w-4" />
                  National Initiative
                </div>
                <h2 className="font-montserrat text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                  Bharat Climate Contribution Initiative
                </h2>
                <p className="text-xl text-orange-600 font-medium mb-6">
                  One Credit for the Planet 🇮🇳
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  The Bharat Climate Contribution Initiative enables individuals and institutions 
                  to participate in nation-scale climate action. Your contributions are directed 
                  towards supporting the offset of carbon emissions from government-operated and 
                  public sector activities, including infrastructure, utilities, and essential services.
                </p>
                <p className="text-slate-700 font-medium mb-8 p-4 bg-gradient-to-r from-orange-50 to-green-50 rounded-xl border-l-4 border-orange-500">
                  This is not just a contribution — it is a step towards national climate responsibility.
                </p>
                <Link to="/initiatives/bharat-climate">
                  <Button size="lg" className="bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600 text-white shadow-lg">
                    Contribute to India's Climate Future
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              
              {/* Visual */}
              <div className="bg-gradient-to-br from-orange-100 via-white to-green-100 p-8 lg:p-12 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-8xl mb-6">🇮🇳</div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-4">
                      <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                        <p className="text-3xl font-bold text-orange-600">2070</p>
                        <p className="text-xs text-slate-600">Net Zero Target</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                        <p className="text-3xl font-bold text-green-600">45%</p>
                        <p className="text-xs text-slate-600">Emission Cut by 2030</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Leaf className="h-12 w-12 mx-auto mb-6 text-emerald-400" />
          <h2 className="font-montserrat text-3xl font-bold mb-4">
            Every Contribution is Certified
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            After each contribution, receive a verified digital certificate with your name, 
            amount, CO₂ offset, and unique ID. Download as PDF or share on social media.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/emission-calculator">
              <Button variant="outline" size="lg" className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10">
                Calculate Your Footprint First
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
